/**
 * Extract IKEA Italia invoice article rows from PDF text ("N. articolo" → SKU + qty).
 * Uses pdf.js text positions so columns survive extraction better than raw string concat.
 */

function parseItalianQuantity(raw) {
  const t = String(raw || '').trim();
  if (!t) return 0;
  if (/^\d+\.\d{1,2}$/.test(t)) {
    return parseFloat(t) || 0;
  }
  if (t.includes(',') && !t.includes('.')) {
    return parseFloat(t.replace(',', '.')) || 0;
  }
  return parseFloat(t.replace(/\./g, '').replace(',', '.')) || 0;
}

/** IKEA invoice row matched as one space-separated string (common in OCR output). */
const OCR_ARTICLE_ROW_RE = /^(\d{8})\s+(.+?)\s+(\d{1,3})%\s+([\d.,]+)/;

/** Italian-formatted currency / quantity string → number (e.g. 1.234,56 or 12,99). */
export function parseItalianMoney(raw) {
  const n = parseItalianQuantity(raw);
  return Number.isFinite(n) ? n : null;
}

/**
 * Group pdf.js text items into approximate horizontal lines (tab-separated within line).
 */
export function clusterPdfTextItemsToLines(items, yTolerance = 3.5) {
  if (!items || items.length === 0) return [];
  const positioned = [];
  for (let i = 0; i < items.length; i += 1) {
    const it = items[i];
    const s = String(it.str || '').trim();
    if (!s) continue;
    positioned.push({
      str: s,
      x: it.transform[4],
      y: it.transform[5],
    });
  }
  positioned.sort((a, b) => {
    if (Math.abs(a.y - b.y) > yTolerance) return b.y - a.y;
    return a.x - b.x;
  });

  const lines = [];
  let bucket = [];
  let anchorY = null;

  const flush = () => {
    if (bucket.length === 0) return;
    bucket.sort((a, b) => a.x - b.x);
    lines.push(bucket.map((x) => x.str).join('\t'));
    bucket = [];
    anchorY = null;
  };

  for (let i = 0; i < positioned.length; i += 1) {
    const it = positioned[i];
    if (anchorY === null || Math.abs(it.y - anchorY) <= yTolerance) {
      bucket.push(it);
      anchorY = anchorY === null ? it.y : anchorY;
    } else {
      flush();
      bucket.push(it);
      anchorY = it.y;
    }
  }
  flush();
  return lines;
}

/**
 * Parse one logical invoice row: 8-digit article, description, VAT %, quantity,
 * optional unit price and line total (Italian decimals) after quantity.
 */
function parseMoneyTokensFromTail(tailText) {
  const tokens = String(tailText || '')
    .replace(/€/g, ' ')
    .split(/\s+/)
    .map((p) => p.trim())
    .filter(Boolean);
  return tokens.filter((p) => /^[\d.,]+$/.test(p));
}

function buildInvoiceArticleRow({ sku, label, vatPct, qty, tailText }) {
  const moneyTokens = parseMoneyTokensFromTail(tailText);
  let unitPriceInclusive = null;
  let lineTotalInclusive = null;
  if (moneyTokens.length >= 2) {
    unitPriceInclusive = parseItalianMoney(moneyTokens[0]);
    lineTotalInclusive = parseItalianMoney(moneyTokens[1]);
  } else if (moneyTokens.length === 1) {
    lineTotalInclusive = parseItalianMoney(moneyTokens[0]);
    if (lineTotalInclusive != null && qty > 0) {
      unitPriceInclusive = Math.round((lineTotalInclusive / qty) * 100) / 100;
    }
  }

  return {
    sku,
    qty,
    label: label || sku,
    vatPct: Number.isFinite(vatPct) ? vatPct : null,
    unitPriceInclusive,
    lineTotalInclusive,
  };
}

export function parseInvoiceArticleRow(line) {
  const raw = String(line || '').trim();
  if (!raw) return null;

  const ocrMatch = raw.match(OCR_ARTICLE_ROW_RE);
  if (ocrMatch) {
    const sku = ocrMatch[1];
    const label = ocrMatch[2].trim();
    const vatPct = parseFloat(ocrMatch[3]);
    const qty = parseItalianQuantity(ocrMatch[4]);
    if (Number.isFinite(qty) && qty > 0 && qty <= 999999) {
      return buildInvoiceArticleRow({
        sku,
        label,
        vatPct,
        qty,
        tailText: raw.slice(ocrMatch[0].length),
      });
    }
  }

  let parts = raw.split(/\t+/).map((p) => p.trim()).filter(Boolean);
  if (parts.length < 4) {
    parts = raw.split(/\s{2,}/).map((p) => p.trim()).filter(Boolean);
  }
  if (parts.length < 4) return null;

  const sku = parts[0];
  if (!/^\d{8}$/.test(sku)) return null;

  const pctIdx = parts.findIndex((p) => /^\d{1,3}%$/.test(p));
  if (pctIdx <= 1 || pctIdx >= parts.length - 1) return null;

  const qtyRaw = parts[pctIdx + 1];
  if (!/^[\d.,]+$/.test(qtyRaw)) return null;

  const qty = parseItalianQuantity(qtyRaw);
  if (!Number.isFinite(qty) || qty <= 0 || qty > 999999) return null;

  const label = parts.slice(1, pctIdx).join(' ').trim() || sku;
  const vatPctRaw = String(parts[pctIdx] || '').replace(/%/g, '').trim();
  const vatPct = vatPctRaw ? parseFloat(vatPctRaw.replace(',', '.')) : null;

  return buildInvoiceArticleRow({
    sku,
    label,
    vatPct,
    qty,
    tailText: parts.slice(pctIdx + 2).join(' '),
  });
}

export function mergeInvoiceRows(rows) {
  const map = new Map();
  rows.forEach((row) => {
    const sku = String(row.sku || '').trim();
    if (!sku) return;
    const prev = map.get(sku) || { sku, qty: 0, label: row.label || sku };
    prev.qty += Number(row.qty) || 0;
    if ((!prev.label || prev.label === sku) && row.label) prev.label = row.label;
    map.set(sku, prev);
  });
  return [...map.values()];
}

function extractInvoiceNumberFromLine(line) {
  const m = String(line).match(/(?:Numero|num\.?)\s*(MA_\d{4}_\d+)/i);
  return m ? m[1] : null;
}

const OCR_RENDER_SCALE = 2;

async function ocrPdfPageToLines(page) {
  if (typeof document === 'undefined') return [];

  const viewport = page.getViewport({ scale: OCR_RENDER_SCALE });
  const canvas = document.createElement('canvas');
  canvas.width = viewport.width;
  canvas.height = viewport.height;
  const ctx = canvas.getContext('2d', { willReadFrequently: true });
  await page.render({ canvasContext: ctx, viewport }).promise;

  const { createWorker } = await import('tesseract.js');
  const worker = await createWorker('ita+eng');
  try {
    const { data: { text } } = await worker.recognize(canvas);
    return text.split(/\r?\n/).map((l) => l.trim()).filter(Boolean);
  } finally {
    await worker.terminate();
  }
}

function parseLinesFromPdfText(textLines, extracted, invoiceNumberRef) {
  for (let li = 0; li < textLines.length; li += 1) {
    const line = textLines[li];
    const inv = extractInvoiceNumberFromLine(line);
    if (inv) invoiceNumberRef.value = inv;

    const parsed = parseInvoiceArticleRow(line);
    if (parsed) extracted.push(parsed);
  }
}

/**
 * @param {File} file
 * @param {{ forPurchaseBill?: boolean }} [options] If true, lines are only `{ sku, qty, label }` merged by SKU (for Xero bills priced from the product catalog server-side).
 * @returns {Promise<{ invoiceNumber: string | null, lines: object[] }>}
 */
export async function extractIkeaInvoiceLinesFromPdf(file, options = {}) {
  const pdfjsLib = await import('pdfjs-dist/build/pdf.min.mjs');

  if (typeof window !== 'undefined') {
    const ver = pdfjsLib.version || '4.10.38';
    pdfjsLib.GlobalWorkerOptions.workerSrc = `https://unpkg.com/pdfjs-dist@${ver}/build/pdf.worker.min.mjs`;
  }

  const data = await file.arrayBuffer();
  const pdf = await pdfjsLib.getDocument({ data }).promise;

  const invoiceNumberRef = { value: null };
  const extracted = [];

  for (let pi = 1; pi <= pdf.numPages; pi += 1) {
    const page = await pdf.getPage(pi);
    const tc = await page.getTextContent();
    const textLines = clusterPdfTextItemsToLines(tc.items);
    parseLinesFromPdfText(textLines, extracted, invoiceNumberRef);
  }

  if (extracted.length === 0) {
    for (let pi = 1; pi <= pdf.numPages; pi += 1) {
      const page = await pdf.getPage(pi);
      const ocrLines = await ocrPdfPageToLines(page);
      parseLinesFromPdfText(ocrLines, extracted, invoiceNumberRef);
    }
  }

  const invoiceNumber = invoiceNumberRef.value;

  const forPurchaseBill = options.forPurchaseBill === true;
  return {
    invoiceNumber,
    lines: forPurchaseBill
      ? mergeInvoiceRows(extracted).map((r) => ({
          sku: r.sku,
          qty: r.qty,
          label: r.label,
        }))
      : mergeInvoiceRows(extracted),
  };
}
